/**
 * AES-256-GCM + PBKDF2-HMAC-SHA256, via WebCrypto ONLY.
 *
 * Nothing here may import from `node:*`. The exact same code runs in Node 20+
 * (the agent) and in the browser (the PWA) — that is what makes the wire format
 * trustworthy: there is only one implementation to get right, and the browser
 * decrypt path is the one the tests exercise. Pinned by test/isomorphic.test.ts.
 */

/** CLAUDE.md §6: MUST default to 310000. */
export const KDF_ITERATIONS_DEFAULT = 310_000;
export const KDF_HASH = "SHA-256";
export const SALT_BYTES = 16;
export const IV_BYTES = 12;
export const TAG_BYTES = 16;

/** Raised for any failure to open a blob: wrong passphrase, tampering, or truncation. */
export class DecryptionError extends Error {
  constructor(message = "could not decrypt: wrong passphrase or corrupted data") {
    super(message);
    this.name = "DecryptionError";
  }
}

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c || !c.subtle) {
    throw new Error(
      "WebCrypto is unavailable — globalThis.crypto.subtle is required (Node 20+ or a secure browser context)",
    );
  }
  return c.subtle;
}

export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

export const utf8 = {
  encode: (text: string): Uint8Array => new TextEncoder().encode(text),
  decode: (bytes: Uint8Array): string => new TextDecoder().decode(bytes),
};

/**
 * Derive the AES key from a passphrase. Non-extractable: the raw key material
 * can never be read back out, so it cannot be logged or persisted by accident.
 */
export async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  iterations: number = KDF_ITERATIONS_DEFAULT,
): Promise<CryptoKey> {
  if (!Number.isInteger(iterations) || iterations < 1) {
    throw new Error(`invalid kdfIterations: ${iterations}`);
  }
  const material = await subtle().importKey(
    "raw",
    utf8.encode(passphrase) as BufferSource,
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return subtle().deriveKey(
    { name: "PBKDF2", salt: salt as BufferSource, iterations, hash: KDF_HASH },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Returns ciphertext WITH the 16-byte GCM tag appended (WebCrypto's own layout). */
export async function aesGcmEncrypt(
  key: CryptoKey,
  iv: Uint8Array,
  plaintext: Uint8Array,
): Promise<Uint8Array> {
  const out = await subtle().encrypt(
    { name: "AES-GCM", iv: iv as BufferSource, tagLength: TAG_BYTES * 8 },
    key,
    plaintext as BufferSource,
  );
  return new Uint8Array(out);
}

/** Input must be ciphertext WITH the tag appended. Any failure throws DecryptionError. */
export async function aesGcmDecrypt(
  key: CryptoKey,
  iv: Uint8Array,
  ciphertextWithTag: Uint8Array,
): Promise<Uint8Array> {
  try {
    const out = await subtle().decrypt(
      { name: "AES-GCM", iv: iv as BufferSource, tagLength: TAG_BYTES * 8 },
      key,
      ciphertextWithTag as BufferSource,
    );
    return new Uint8Array(out);
  } catch {
    // Deliberately swallow the underlying error: it distinguishes nothing useful
    // and a wrong passphrase must not be reported differently from tampering.
    throw new DecryptionError();
  }
}

export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest("SHA-256", bytes as BufferSource));
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  return toHex(await sha256(bytes));
}

export function toHex(bytes: Uint8Array): string {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}

/** Constant-time-ish comparison for hex digests and tokens. */
export function timingSafeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}
